import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, useAuth } from "../App";
import { SUPPORTED_LANGUAGES, LANG_STORAGE_KEY } from "../i18n";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * <LanguageSwitcher /> — drop-in dropdown that swaps the UI language between
 * Spanish and English (and any future locales registered in /i18n/index.js).
 *
 * Behavior:
 *   - Guests: persists choice in localStorage (i18next-browser-languagedetector
 *     handles this automatically via the LANG_STORAGE_KEY).
 *   - Authenticated users: ALSO PATCHes `/api/auth/language` so all server-side
 *     transactional emails / SMS / notifications use the same locale.
 *   - On login, the AuthProvider syncs i18n to the user's stored language (see
 *     App.js useEffect below).
 *
 * Variants:
 *   - `compact`: icon-only trigger (used in the mobile BottomNav / FAB menu).
 *   - default: "ES / EN" label visible next to the globe (used in the Navbar).
 */
export default function LanguageSwitcher({ compact = false, className = "" }) {
  const { i18n, t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const current = (i18n.resolvedLanguage || i18n.language || "es").slice(0, 2);

  const changeLanguage = useCallback(
    async (code) => {
      if (!code || code === current) return;
      try {
        await i18n.changeLanguage(code);
        try {
          localStorage.setItem(LANG_STORAGE_KEY, code);
        } catch (_e) {
          /* localStorage disabled — skip */
        }
        if (isAuthenticated) {
          // Persist to the user account so emails / SMS / notifications match.
          try {
            await api.put("/auth/language", { language: code });
          } catch (_e) {
            /* don't block the UI swap on a network error */
          }
        }
      } catch (_e) {
        /* swallow */
      }
    },
    [i18n, current, isAuthenticated],
  );

  // First-mount sync: if the user is logged in and has NO explicit device
  // preference (no ?lng override, nothing in localStorage), adopt the
  // server-stored language. An explicit local choice always wins — otherwise
  // logging in would silently undo the language the user just picked.
  useEffect(() => {
    if (!user) return;
    let localPref = null;
    try {
      localPref = localStorage.getItem(LANG_STORAGE_KEY);
    } catch (_e) {
      /* skip */
    }
    const urlLang = new URLSearchParams(window.location.search).get("lng");
    if (urlLang || localPref) return;
    const serverLang = (user.language || "").slice(0, 2);
    if (serverLang && serverLang !== current && (serverLang === "es" || serverLang === "en")) {
      i18n.changeLanguage(serverLang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, serverLang);
      } catch (_e) {
        /* skip */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-switcher-trigger"
        aria-label={t("common.language")}
        className={
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta " +
          className
        }
      >
        <Globe className="w-4 h-4" />
        {!compact && (
          <span className="uppercase tracking-wide text-xs">{current}</span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {SUPPORTED_LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => changeLanguage(l.code)}
            data-testid={`language-option-${l.code}`}
            className="flex items-center justify-between gap-2 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden>{l.flag}</span>
              <span>{l.label}</span>
            </span>
            {current === l.code && <Check className="w-4 h-4 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
